import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import { FormattedCustomDate } from "components/common/FormattedComponents";
import { injectIntl, intlShape } from "react-intl";
import { DataTable } from "@cxagroup/casper";
import { parseTemplate } from "cxa-utils";
import humps from "humps";
import lodash from "lodash";
import messages from "components/messagesDescription";
import { decodeHtmlContent, renderAsHTML } from "../../utils";
import { formatMoney } from "../employee/shop/EnrollmentDataUtil";
import StandardMoney from "../common/StandardMoney";

const getFormattedValueMap = (
  { planId, accountType, periodEnd, currency, memberId, memberType },
  intl,
  alwaysUnlimited = false,
  hideCurrency = false,
  selectedDelegator
) => ({
  // this gets called a wee bit too often, chance to optimise later
  date: value => <FormattedCustomDate value={value} />,
  currency: (value, customCurrency) => {
    if (!lodash.isNumber(value)) {
      return null;
    }
    if (hideCurrency) {
      if (value == "999999999999") {
        return intl.formatMessage(messages.unlimitedDescription);
      } else {
        return formatMoney(value);
      }
    }
    const currencyOption = customCurrency || currency;
    return <StandardMoney value={value} currency={currencyOption} isUnlimited={alwaysUnlimited} />;
  },
  link: value => {
    const href = `/claims/accounts/${planId}/${accountType}/${
      periodEnd ? encodeURIComponent(periodEnd) : ""
    }?memberId=${memberId}&memberType=${memberType}`;
    const trueValue = value ? intl.formatMessage({ id: value, defaultMessage: value }) : "";

    if (selectedDelegator && selectedDelegator.toLowerCase() !== "myself") {
      return trueValue;
    }

    return <Link to={href}>{trueValue}</Link>;
  },
  transactionDesc: (category, description) => {
    // Needed to add a check to translate "Claim Reimbursement" + the claimId
    const claimReimbursementText = "Claim Reimbursement";

    if (category === claimReimbursementText) {
      return description.replace(
        claimReimbursementText,
        intl.formatMessage(messages.claimReimbursementDescription)
      );
    }

    return intl.formatMessage({ id: description, defaultMessage: description });
  },
});

const AccountsList = ({
  rows,
  columns,
  intl,
  formatters = {},
  currency,
  selectedDelegator,
  getDonateCategory,
  hideCurrency = false,
}) => {
  if (!rows || rows.length < 1) {
    return <p>{intl.formatMessage(messages.noAccountsDescription)}</p>;
  }

  const headers =
    !!columns &&
    columns.length > 0 &&
    columns.map(
      ({ shouldDisplay, displayLabel }) =>
        !!shouldDisplay &&
        intl.formatMessage({
          id: displayLabel,
          defaultMessage: displayLabel,
        })
    );

  const mappedRows = rows.map(
    ({ ...row }) =>
      !!columns &&
      columns.length > 0 &&
      columns.map(
        ({ shouldDisplay, fieldID, format: columnFormat, isIntlSkipped = false, ...column }) => {
          if (!shouldDisplay) {
            return null;
          }

          const formatter = lodash.isFunction(formatters)
            ? formatters
            : lodash.get(formatters, fieldID, false);
          let cellData;
          if (formatter) {
            const formatter = lodash.isFunction(formatters)
              ? formatters
              : lodash.get(formatters, fieldID, false);
            cellData = lodash.isFunction(formatter)
              ? formatter(row, column, intl, selectedDelegator, getDonateCategory)
              : {};
          } else {
            let fldname = humps.camelize(fieldID);
            let enforceUnlimited = row.isUnlimited && fldname === "balance";
            let rawCellData = row[fldname];

            // Currency type can be set on row level as either currency or currencyType
            // Or currency can be set on the account level
            // Accept row currency if provided otherwise use account currency
            const rowCurrency = row.currency || row.currencyType || currency;

            cellData = {
              html: !lodash.isEmpty(columnFormat)
                ? parseTemplate(
                    columnFormat,
                    getFormattedValueMap(
                      { currency: rowCurrency, ...row },
                      intl,
                      enforceUnlimited,
                      hideCurrency,
                      selectedDelegator
                    )
                  )(row)
                : rawCellData
                ? isIntlSkipped
                  ? rawCellData
                  : intl.formatMessage({
                      id: rawCellData,
                      defaultMessage: rawCellData,
                    })
                : "",
            };
            if (fldname == "accountOwner" || fldname == "receiptNo") {
              cellData.html = decodeHtmlContent(cellData.html);
            }
            if (fldname == "adminRemarks") {
              cellData.html = renderAsHTML("span", cellData.html);
            }
          }
          // Class names don't seem to be used here from what I can tell
          // If they are, we'll need to modify Casper/DataTable to accommodate
          //className={classnames(cellData.className)}
          return cellData.html;
        }
      )
  );

  return (
    <div className="apl-scroll-table">
      <DataTable headers={headers} rows={mappedRows} />
    </div>
  );
};

AccountsList.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  formatters: PropTypes.any,
  currency: PropTypes.string,
  intl: intlShape.isRequired,
  selectedDelegator: PropTypes.string,
  getDonateCategory: PropTypes.bool,
  hideCurrency: PropTypes.bool,
};

export default injectIntl(AccountsList);



// WEBPACK FOOTER //
// ./components/claim/AccountsList.js